import {
  TransactionAnomalyPriority as TransactionAnomalyPriorityEnum,
  TransactionCategoryDetailedType,
} from '../../__generated__/globalTypes';

import type { AdminGetTransactionsForReviewQuery } from './__generated__/TransactionAnomalyFeedbackScene.gql';

export type TransactionNode =
  AdminGetTransactionsForReviewQuery['adminGetTransactionsForFamily']['edges'][number]['node'];

export type MerchantStatsRow =
  AdminGetTransactionsForReviewQuery['adminGetLatestTransactionMerchantStatistics'][number];

export interface IPriorityTone {
  border: string;
  bg: string;
  text: string;
}

export interface IPriorityBuckets {
  unreviewed: TransactionNode[];
  reviewed: TransactionNode[];
}

export const PRIORITY_ORDER: TransactionAnomalyPriorityEnum[] = [
  TransactionAnomalyPriorityEnum.P0,
  TransactionAnomalyPriorityEnum.P1,
  TransactionAnomalyPriorityEnum.P2,
  TransactionAnomalyPriorityEnum.P3,
  TransactionAnomalyPriorityEnum.P4,
];

export const PRIORITY_LABELS: Record<TransactionAnomalyPriorityEnum, string> = {
  [TransactionAnomalyPriorityEnum.P0]: 'P0 · Urgent (score 10)',
  [TransactionAnomalyPriorityEnum.P1]: 'P1 · High (score ≥9)',
  [TransactionAnomalyPriorityEnum.P2]: 'P2 · Medium (score ≥8)',
  [TransactionAnomalyPriorityEnum.P3]: 'P3 · Low (score ≥6)',
  [TransactionAnomalyPriorityEnum.P4]: 'P4 · Not an anomaly (score <6)',
};

// Traffic-light data viz for the priority heatmap, intentionally diverging from
// the MOPS single-accent rule (which applies to chrome, not encoded data).
// Stops are tuned for the dark-only theme.
export const PRIORITY_TONE: Record<TransactionAnomalyPriorityEnum, IPriorityTone> = {
  [TransactionAnomalyPriorityEnum.P0]: { border: 'border-l-red-500', bg: 'bg-red-950/30', text: 'text-red-200' },
  [TransactionAnomalyPriorityEnum.P1]: {
    border: 'border-l-yellow-500',
    bg: 'bg-yellow-950/30',
    text: 'text-yellow-200',
  },
  [TransactionAnomalyPriorityEnum.P2]: { border: 'border-l-blue-500', bg: 'bg-blue-950/30', text: 'text-blue-200' },
  [TransactionAnomalyPriorityEnum.P3]: { border: 'border-l-green-500', bg: 'bg-green-950/30', text: 'text-green-200' },
  [TransactionAnomalyPriorityEnum.P4]: {
    border: 'border-l-gray-500',
    bg: 'bg-bg-secondary',
    text: 'text-fg-secondary',
  },
};

export function getPriorityFromScore(score: number): TransactionAnomalyPriorityEnum {
  if (score === 10) {
    return TransactionAnomalyPriorityEnum.P0;
  }
  if (score >= 9) {
    return TransactionAnomalyPriorityEnum.P1;
  }
  if (score >= 8) {
    return TransactionAnomalyPriorityEnum.P2;
  }
  if (score >= 6) {
    return TransactionAnomalyPriorityEnum.P3;
  }
  return TransactionAnomalyPriorityEnum.P4;
}

export function resolvePriority(transaction: TransactionNode): TransactionAnomalyPriorityEnum {
  if (transaction.anomalyFeedback?.priority) {
    return transaction.anomalyFeedback.priority;
  }
  const isCreditCardPayment =
    transaction.category.detailedType === TransactionCategoryDetailedType.LoanPaymentsCreditCardPayment;
  const score = !isCreditCardPayment && transaction.anomalyDetection ? transaction.anomalyDetection.anomalyScore : 0;
  return getPriorityFromScore(score);
}
